import { useEffect, useState } from 'react';

/* Bootstrap 5's breakpoints, kept here so the JS branches and the CSS media
 * queries in theme.css change the layout at exactly the same widths. */
export const BREAKPOINTS = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
} as const;

/**
 * Subscribe to a media query.
 *
 * The forms previously branched on `window.screen.width`, which is the size of
 * the physical display — it does not change when the window is resized or the
 * phone is rotated, and on a desktop it reports the full monitor width even in
 * a narrow window. Reading it during render also meant the value was captured
 * once and never updated. matchMedia measures the viewport and re-renders on
 * change, so the layout now tracks the window it is actually drawn in.
 */
export const useMediaQuery = (query: string): boolean => {
    const [matches, setMatches] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(query).matches,
    );

    useEffect(() => {
        const mql = window.matchMedia(query);
        const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);

        // The query may have changed since the initial state was computed.
        setMatches(mql.matches);

        // addListener is deprecated but is the only option on Safari < 14.
        if (mql.addEventListener) {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }
        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [query]);

    return matches;
};

/** Phones — below Bootstrap's `sm`, where every column stacks. */
export const useIsMobile = () => useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 0.02}px)`);

/** Phones and small tablets — below `md`, where three-up rows become two-up. */
export const useIsCompact = () => useMediaQuery(`(max-width: ${BREAKPOINTS.md - 0.02}px)`);

/**
 * Below `lg`, where the fuel grids stop being three-across.
 *
 * Those rows read as a table: a "Type / Quantity" header row sits above the
 * fields and only lines up while every row has the same number of columns. Once
 * the columns wrap, the headers are dropped and each control carries its own
 * placeholder instead, so a field is never left unlabelled.
 */
export const useIsStacked = () => useMediaQuery(`(max-width: ${BREAKPOINTS.lg - 0.02}px)`);
